// Rotas do app do cliente. Sessão via MeProvider; sem sessão → /entrar; conta nova → /boas-vindas.
import type { ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router';
import { MeProvider, useMe } from './me';
import { Shell } from './components/Shell';
import { Gate } from './pages/Gate';
import { Onboarding } from './pages/Onboarding';
import { Chat } from './pages/Chat';
import { Agents } from './pages/Agents';
import { Documents } from './pages/Documents';
import { Library } from './pages/Library';
import { Dashboard } from './pages/Dashboard';
import { Keys } from './pages/Keys';
import { Usage } from './pages/Usage';
import { Audio } from './pages/Audio';
import { ImageGen } from './pages/ImageGen';
import { Settings } from './pages/Settings';
import { Company, CompanyReports } from './pages/Company';
import { Invite } from './pages/Invite';

/** Página que exige sessão (e onboarding concluído), dentro da casca. */
function Private({ children }: { children: ReactNode }) {
  const { me, loading } = useMe();
  const loc = useLocation();
  if (loading) return <p className="mute" style={{ padding: 24 }}>carregando…</p>;
  if (!me) return <Navigate to="/entrar" replace state={{ from: loc.pathname }} />;
  if (!me.onboardedAt) return <Navigate to="/boas-vindas" replace />;
  return <Shell>{children}</Shell>;
}

function OnboardingRoute() {
  const { me, loading } = useMe();
  if (loading) return null;
  if (!me) return <Navigate to="/entrar" replace />;
  if (me.onboardedAt) return <Navigate to="/chaves" replace />;
  return <Onboarding />;
}

/** Conversa dentro de um agente: mesma UI do Chat, com a lista e o system prompt do agente. */
function AgentChatRoute() {
  const { agentId } = useParams<{ agentId: string }>();
  return <Chat agentId={agentId} />;
}

function GateRoute() {
  const { me, loading } = useMe();
  if (loading) return null;
  if (me) return <Navigate to={me.onboardedAt ? '/chat' : '/boas-vindas'} replace />;
  return <Gate />;
}

export function App() {
  return (
    <MeProvider>
      <Routes>
        <Route path="/entrar" element={<GateRoute />} />
        <Route path="/boas-vindas" element={<OnboardingRoute />} />
        <Route path="/convite/:token" element={<Invite />} />   {/* pública: quem recebeu o link ainda não tem conta */}
        
        {/* Ferramentas IA */}
        <Route path="/chat" element={<Private><Chat /></Private>} />
        <Route path="/chat/:id" element={<Private><Chat /></Private>} />
        <Route path="/audio" element={<Private><Audio /></Private>} />
        <Route path="/imagem" element={<Private><ImageGen /></Private>} />
        <Route path="/agentes" element={<Private><Agents /></Private>} />
        <Route path="/agentes/:agentId" element={<Private><AgentChatRoute /></Private>} />
        <Route path="/agentes/:agentId/:id" element={<Private><AgentChatRoute /></Private>} />
        <Route path="/documentos" element={<Private><Documents /></Private>} />
        <Route path="/biblioteca" element={<Private><Library /></Private>} />
        
        {/* Gestão */}
        <Route path="/chaves" element={<Private><Keys /></Private>} />
        <Route path="/uso" element={<Private><Usage /></Private>} />
        <Route path="/configuracoes" element={<Private><Settings /></Private>} />
        <Route path="/empresa" element={<Private><Company /></Private>} />
        <Route path="/empresa/relatorios" element={<Private><CompanyReports /></Private>} />
        <Route path="/conta" element={<Navigate to="/configuracoes" replace />} />
        
        <Route path="/" element={<Private><Dashboard /></Private>} />
        <Route path="*" element={<Private><p>página não encontrada</p></Private>} />
      </Routes>
    </MeProvider>
  );
}
